// src/hooks/useBatchDownload.js
import { useState, useCallback } from 'react';
import { downloadInvoicesAsZip } from '../utils/invoiceZipDownloader';

/**
 * Hook for managing batch invoice download state and operations
 * @returns {Object} Download state and functions
 */
export function useBatchDownload() {
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, currentInvoice: null });
  const [errors, setErrors] = useState([]);
  const [result, setResult] = useState(null);

  /**
   * Download invoices as a ZIP file
   * @param {Array} invoices - Array of invoice objects
   * @param {string} startDate - Start date
   * @param {string} endDate - End date
   */
  const downloadInvoicesAsZipFile = useCallback(async (invoices, startDate, endDate) => {
    try {
      setDownloading(true);
      setErrors([]);
      setResult(null);
      setProgress({ current: 0, total: invoices.length, currentInvoice: null });

      console.log('useBatchDownload: Starting batch download', {
        count: invoices.length,
        dateRange: `${startDate} to ${endDate}`
      });

      // Progress callback
      const onProgress = (current, total, currentInvoice) => {
        setProgress({ current, total, currentInvoice });
      };

      // Download as ZIP
      const downloadResult = await downloadInvoicesAsZip(
        invoices,
        startDate,
        endDate,
        onProgress
      );

      setResult(downloadResult);
      setErrors(downloadResult.errors || []);

      console.log('useBatchDownload: Batch download complete', {
        successCount: downloadResult.successCount,
        totalCount: downloadResult.totalCount,
        errorCount: downloadResult.errors?.length || 0
      });

      return downloadResult;

    } catch (error) {
      console.error('useBatchDownload: Error during batch download', error);
      setErrors([{ error: error.message }]);
      throw error;

    } finally {
      setDownloading(false);
      // Keep progress visible for a moment
      setTimeout(() => {
        setProgress({ current: 0, total: 0, currentInvoice: null });
      }, 1000);
    }
  }, []);

  /**
   * Reset download state
   */
  const reset = useCallback(() => {
    setDownloading(false);
    setProgress({ current: 0, total: 0, currentInvoice: null });
    setErrors([]);
    setResult(null);
  }, []);

  return {
    downloading,
    progress,
    errors,
    result,
    downloadInvoicesAsZipFile,
    reset
  };
}
